import { useState } from "react";
import { Image, View } from "react-native";
import {
  CalendarClock,
  Egg,
  Plus,
  RefreshCw,
  Sparkles,
} from "lucide-react-native";
import {
  CardSurface,
  ClinicalDataStatus,
  CompactActionButton,
  EmptyState,
  MoonSkeleton,
  MoonText,
} from "@louise/moon-ui";
import {
  useFrozenSpecimens,
  useOocyteCohorts,
} from "@src/packages/care/cryovault/useSpecimens";
import {
  buildPostCycleInventorySnapshot,
  resolvePostCycleSectionState,
} from "@src/packages/cycles/presenters/postCycleState";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import {
  projectFertilityPrediction,
  type ObservedValue,
} from "@src/packages/fertility-projections";
import {
  OutcomeDotFunnelRows,
  type OutcomeDotFunnelStage,
} from "./OutcomeDotFunnelRows";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@louise/moon-ui/primitives/Accordion";
import { FunnelScopeTabs, type FunnelScopeTabOption } from "./FunnelScopeTabs";
import { semanticColors } from "@louise/theme/tokens";
import type { PlanAction } from "../plan.types";
import cryoVaultFridgeIllustration from "@src/packages/moon-ui/assets/images/cryo-vault-fridge.png";

type TabId = "this-cycle" | "all-eggs" | "all-embryos";

// Visible artwork bounds in the 600×631 source. Clip the asymmetric transparent
// margins and stray top-left fragment here, without changing other asset users.
const VAULT_ARTWORK = {
  sourceWidth: 600,
  sourceHeight: 631,
  left: 137,
  top: 79,
  width: 463,
  height: 552,
};
// Keep the visible vault at its previous ~140px height, not the padded PNG size.
const VAULT_ARTWORK_SCALE = 140 / VAULT_ARTWORK.height;

function eggFunnelStages(
  start: number,
  t: TranslationFunction,
  sourceCycleId?: string,
): OutcomeDotFunnelStage[] {
  const frozen: ObservedValue<number> = {
    value: start,
    provenance: {
      sourceType: "clinic_record",
      sourceId: sourceCycleId ?? "cryo-vault-total",
    },
  };
  const projection = projectFertilityPrediction({
    kind: "frozen_oocyte_cohort",
    patient: {},
    oocytes: { frozen, sourceCycleId },
    goal: { kind: "expected_stage_count", stage: "live_births" },
  });
  const value = (id: string) =>
    Math.max(
      0,
      Math.round(
        projection.stages.find((stage) => stage.id === id)?.estimate.mean ?? 0,
      ),
    );
  const hasStage = (id: string) =>
    projection.stages.some((stage) => stage.id === id);
  const surviveThaw = value("thawed_oocytes");
  const fertilize = value("fertilized_oocytes");
  const blastocysts = value("blastocysts");
  const euploids = value("euploid_embryos");
  const liveBirthProbability =
    projection.probabilityAtLeastOneLiveBirth?.mean ?? null;
  return [
    {
      id: "frozen-eggs",
      label: t("homepage.review_concept.future_use_frozen_eggs"),
      value: start,
      detail: t("homepage.review_concept.future_use_stored_now"),
    },
    {
      id: "survive-thaw",
      label: t("homepage.review_concept.future_use_survive_thaw"),
      value: surviveThaw,
      detail: t("homepage.review_concept.future_use_may_not_survive_thawing", {
        params: { count: String(Math.max(0, start - surviveThaw)) },
      }),
      missing: !hasStage("thawed_oocytes"),
    },
    {
      id: "fertilize",
      label: t("homepage.review_concept.future_use_fertilize"),
      value: fertilize,
      detail: t("homepage.review_concept.future_use_may_not_fertilize", {
        params: { count: String(Math.max(0, surviveThaw - fertilize)) },
      }),
      missing: !hasStage("fertilized_oocytes"),
    },
    {
      id: "blastocysts",
      label: t("homepage.review_concept.future_use_blastocysts"),
      value: blastocysts,
      detail: t(
        "homepage.review_concept.future_use_may_stop_before_blastocyst",
        {
          params: { count: String(Math.max(0, fertilize - blastocysts)) },
        },
      ),
      missing: !hasStage("blastocysts"),
    },
    {
      id: "euploids",
      label: t("homepage.review_concept.future_use_euploid_embryos"),
      value: euploids,
      detail: t(
        "homepage.review_concept.future_use_may_be_aneuploid_or_untested",
        {
          params: { count: String(Math.max(0, blastocysts - euploids)) },
        },
      ),
      missing: !hasStage("euploid_embryos"),
    },
    {
      id: "live-birth",
      label: t("homepage.review_concept.future_use_live_birth_estimate"),
      value:
        liveBirthProbability == null
          ? 0
          : Math.max(0, Math.round(euploids * liveBirthProbability)),
      displayValue:
        liveBirthProbability == null
          ? undefined
          : `${Math.round(liveBirthProbability * 100)}%`,
      detail: t("homepage.review_concept.future_use_live_birth_baseline_note"),
      missing: liveBirthProbability == null,
    },
  ];
}

function embryoFunnelStages(
  start: number,
  t: TranslationFunction,
  sourceCycleId?: string,
): OutcomeDotFunnelStage[] {
  const frozen: ObservedValue<number> = {
    value: start,
    provenance: {
      sourceType: "clinic_record",
      sourceId: sourceCycleId ?? "cryo-vault-total",
    },
  };
  const projection = projectFertilityPrediction({
    kind: "embryo_transfer",
    patient: {},
    embryos: { frozen, sourceCycleId },
    goal: { kind: "expected_stage_count", stage: "live_births" },
  });
  const value = (id: string) =>
    Math.max(
      0,
      Math.round(
        projection.stages.find((stage) => stage.id === id)?.estimate.mean ?? 0,
      ),
    );
  const hasStage = (id: string) =>
    projection.stages.some((stage) => stage.id === id);
  const surviveThaw = value("warmed_embryos");
  const transferred = value("transferred_embryos");
  const implanted = value("implanted_embryos");
  const liveBirthProbability =
    projection.probabilityAtLeastOneLiveBirth?.mean ?? null;
  return [
    {
      id: "frozen-embryos",
      label: t("homepage.review_concept.future_use_frozen_embryos"),
      value: start,
      detail: t("homepage.review_concept.future_use_stored_now"),
    },
    {
      id: "survive-thaw",
      label: t("homepage.review_concept.future_use_survive_thaw"),
      value: surviveThaw,
      detail: t("homepage.review_concept.future_use_may_not_survive_warming", {
        params: { count: String(Math.max(0, start - surviveThaw)) },
      }),
      missing: !hasStage("warmed_embryos"),
    },
    {
      id: "transferred",
      label: t("homepage.review_concept.future_use_transferred"),
      value: transferred,
      detail: t(
        "homepage.review_concept.future_use_warmed_embryos_are_available_for_transfer",
      ),
      missing: !hasStage("transferred_embryos"),
    },
    {
      id: "implanted",
      label: t("homepage.review_concept.future_use_implantation_estimate"),
      value: implanted,
      detail: t("homepage.review_concept.future_use_may_not_implant", {
        params: { count: String(Math.max(0, transferred - implanted)) },
      }),
      missing: !hasStage("implanted_embryos"),
    },
    {
      id: "live-birth",
      label: t("homepage.review_concept.future_use_live_birth_estimate"),
      value:
        liveBirthProbability == null
          ? 0
          : Math.max(0, Math.round(transferred * liveBirthProbability)),
      displayValue:
        liveBirthProbability == null
          ? undefined
          : `${Math.round(liveBirthProbability * 100)}%`,
      detail: t(
        "homepage.review_concept.future_use_live_birth_baseline_note_embryos",
      ),
      missing: liveBirthProbability == null,
    },
  ];
}

function FutureUseEmptyState({
  primaryUnit,
  totalEggs,
  totalEmbryos,
  totalSperm,
  onAction,
  state = "empty",
}: {
  primaryUnit: "eggs" | "embryos";
  totalEggs: number;
  totalEmbryos: number;
  totalSperm: number;
  onAction?: (action: PlanAction) => void;
  state?: "empty" | "partial";
}) {
  const t = useTranslations();
  const hasOtherStoredUnit =
    primaryUnit === "eggs" ? totalEmbryos > 0 : totalEggs > 0;
  const hasAnyStoredSpecimens =
    totalEggs > 0 || totalEmbryos > 0 || totalSperm > 0;
  const title =
    state === "partial"
      ? t("homepage.review_concept.future_use_vault_partial_title")
      : !hasAnyStoredSpecimens
        ? t("homepage.review_concept.future_use_vault_is_empty")
        : t(
            primaryUnit === "eggs"
              ? "homepage.review_concept.future_use_no_frozen_eggs_yet"
              : "homepage.review_concept.future_use_no_frozen_embryos_yet",
          );
  const subtitle =
    state === "partial"
      ? t("homepage.review_concept.future_use_vault_partial_subtitle")
      : !hasAnyStoredSpecimens
        ? t(
            "homepage.review_concept.future_use_add_frozen_material_to_see_a_projection",
          )
        : hasOtherStoredUnit
          ? t(
              primaryUnit === "eggs"
                ? "homepage.review_concept.future_use_vault_holds_embryos_not_eggs"
                : "homepage.review_concept.future_use_vault_holds_eggs_not_embryos",
            )
          : totalSperm > 0
            ? t("homepage.review_concept.future_use_vault_holds_sperm_only")
            : t(
                primaryUnit === "eggs"
                  ? "homepage.review_concept.future_use_add_frozen_eggs_to_see_a_projection"
                  : "homepage.review_concept.future_use_add_frozen_embryos_to_see_a_projection",
              );
  const action = onAction ? (
    <View
      style={{
        width: "100%",
        maxWidth: 420,
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8,
      }}
    >
      <CompactActionButton
        leadingIcon={CalendarClock}
        label={t("homepage.review_concept.future_use_view_past_cycles")}
        variant="secondary"
        flexGrow={1}
        minWidth={0}
        onPress={() =>
          onAction({ label: "View past cycles", screen: "AllPastCycles" })
        }
      />
      <CompactActionButton
        leadingIcon={Plus}
        label={t("homepage.review_concept.future_use_add_specimens")}
        action="homepage.review_concept.future_use_add_specimens"
        variant="primary"
        flexGrow={1}
        minWidth={0}
        onPress={() =>
          onAction({ label: "Add specimens", screen: "CryoVaultScreen" })
        }
      />
    </View>
  ) : null;

  return (
    <CardSurface style={{ padding: 16, overflow: "visible" }}>
      <EmptyState
        illustration={
          <View
            testID="future-use-vault-illustration"
            style={{
              width: VAULT_ARTWORK.width * VAULT_ARTWORK_SCALE,
              height: VAULT_ARTWORK.height * VAULT_ARTWORK_SCALE,
              overflow: "hidden",
            }}
          >
            <Image
              source={cryoVaultFridgeIllustration}
              resizeMode="contain"
              accessible={false}
              style={{
                position: "absolute",
                width: VAULT_ARTWORK.sourceWidth * VAULT_ARTWORK_SCALE,
                height: VAULT_ARTWORK.sourceHeight * VAULT_ARTWORK_SCALE,
                left: -VAULT_ARTWORK.left * VAULT_ARTWORK_SCALE,
                top: -VAULT_ARTWORK.top * VAULT_ARTWORK_SCALE,
              }}
            />
          </View>
        }
        title={title}
        titleVariant="featured"
        subtitle={subtitle}
        action={action}
        testID="future-use-empty-state"
        style={{ paddingVertical: 14, paddingHorizontal: 0 }}
      />
    </CardSurface>
  );
}

export function FutureUseEstimateTabs({
  primaryUnit = "eggs",
  sourceCycleId,
  onAction,
}: {
  primaryUnit?: "eggs" | "embryos";
  /** The cycle whose stored specimens should back the cycle-scoped tab. */
  sourceCycleId?: string | null;
  onAction?: (action: PlanAction) => void;
}) {
  const t = useTranslations();
  const frozen = useFrozenSpecimens();
  const cohortsQuery = useOocyteCohorts();
  const { specimens, isLoading, error } = frozen;
  const cohorts = cohortsQuery.data;
  const vaultInventory = buildPostCycleInventorySnapshot(specimens, cohorts, {
    kind: "vault",
  });
  const cycleInventory = sourceCycleId
    ? buildPostCycleInventorySnapshot(specimens, cohorts, {
        kind: "origin_cycle",
        cycleId: sourceCycleId,
      })
    : null;
  const totalEggs = vaultInventory.counts.oocyte;
  const totalEmbryos = vaultInventory.counts.embryo;
  const totalSperm = vaultInventory.counts.semen;
  const actualSelectedCount = primaryUnit === "eggs" ? totalEggs : totalEmbryos;
  const thisCycleEggs = cycleInventory?.counts.oocyte ?? 0;
  const thisCycleEmbryos = cycleInventory?.counts.embryo ?? 0;
  const hasCycleInventory =
    sourceCycleId != null &&
    (primaryUnit === "embryos" ? thisCycleEmbryos > 0 : thisCycleEggs > 0);
  const [active, setActive] = useState<TabId>("this-cycle");
  /*
   * Folded by default: what she owns is the answer, the model is on request.
   *
   * Opening the page on five inferred numbers puts population averages in front
   * of her before her own count has landed — and they are the rows most easily
   * mistaken for facts about her eggs. She asks for them now.
   *
   * Declared above the early returns for loading, error and empty: hooks may
   * not be called conditionally.
   */
  const [estimateOpen, setEstimateOpen] = useState(false);
  const hasData = (specimens?.length ?? 0) > 0 || (cohorts?.length ?? 0) > 0;
  const vaultLoadFailed = !!error || !!cohortsQuery.error;
  const sectionState = resolvePostCycleSectionState({
    isLoading: isLoading || cohortsQuery.isLoading,
    isError: vaultLoadFailed,
    hasData,
  });
  const retryVault = () => {
    void frozen.refetch();
    void cohortsQuery.refetch();
  };
  const isRetryingVault = frozen.isFetching || cohortsQuery.isFetching;
  // Some records loaded and some failed: keep what loaded, and say the rest
  // couldn't load, with Try again.
  const someVaultRecordsFailed = sectionState === "partial" && vaultLoadFailed;
  const vaultStaleRow = someVaultRecordsFailed ? (
    <ClinicalDataStatus
      state="stale"
      message={t(
        "homepage.review_concept.future_use_some_vault_records_couldnt_load",
      )}
      retryLabel={t("clinical_data.retry")}
      retryingLabel={t("clinical_data.retrying")}
      onRetry={retryVault}
      isRetrying={isRetryingVault}
      style={{ marginBottom: 12 }}
      testID="future-use-vault-stale"
    />
  ) : null;

  if (sectionState === "loading") {
    return (
      <View
        testID="future-use-loading-state"
        style={{ gap: 12, paddingTop: 8, paddingBottom: 24 }}
      >
        <MoonSkeleton width="100%" height={38} radius={16} />
        <MoonSkeleton width="100%" height={208} radius={16} />
      </View>
    );
  }

  if (sectionState === "error") {
    return (
      <ClinicalDataStatus
        state="unavailable"
        title={t("homepage.review_concept.future_use_vault_unavailable_title")}
        message={t("clinical_data.unavailable_body")}
        retryLabel={t("clinical_data.retry")}
        retryingLabel={t("clinical_data.retrying")}
        onRetry={retryVault}
        isRetrying={isRetryingVault}
        testID="future-use-vault-unavailable"
      />
    );
  }

  if (sectionState === "partial" && !someVaultRecordsFailed) {
    return (
      <FutureUseEmptyState
        primaryUnit={primaryUnit}
        totalEggs={totalEggs}
        totalEmbryos={totalEmbryos}
        totalSperm={totalSperm}
        onAction={onAction}
        state={sectionState}
      />
    );
  }

  if (actualSelectedCount === 0) {
    return (
      <View>
        {vaultStaleRow}
        <FutureUseEmptyState
          primaryUnit={primaryUnit}
          totalEggs={totalEggs}
          totalEmbryos={totalEmbryos}
          totalSperm={totalSperm}
          onAction={onAction}
        />
      </View>
    );
  }

  const options: FunnelScopeTabOption<TabId>[] =
    primaryUnit === "embryos"
      ? [
          ...(hasCycleInventory
            ? [
                {
                  id: "this-cycle" as const,
                  label: t(
                    "homepage.review_concept.future_use_from_this_cycle",
                  ),
                  icon: RefreshCw,
                },
              ]
            : []),
          {
            id: "all-embryos",
            label: t("homepage.review_concept.future_use_from_all_my_embryos"),
            icon: Sparkles,
          },
        ]
      : [
          ...(hasCycleInventory
            ? [
                {
                  id: "this-cycle" as const,
                  label: t(
                    "homepage.review_concept.future_use_from_this_cycle",
                  ),
                  icon: RefreshCw,
                },
              ]
            : []),
          {
            id: "all-eggs",
            label: t("homepage.review_concept.future_use_from_all_my_eggs"),
            icon: Egg,
          },
          ...(totalEmbryos > 0
            ? [
                {
                  id: "all-embryos" as const,
                  label: t(
                    "homepage.review_concept.future_use_from_all_my_embryos",
                  ),
                  icon: Sparkles,
                },
              ]
            : []),
        ];

  const safeActive = options.some((option) => option.id === active)
    ? active
    : (options[0]?.id ?? "all-eggs");

  const stages =
    safeActive === "all-embryos"
      ? embryoFunnelStages(totalEmbryos, t)
      : safeActive === "all-eggs"
        ? eggFunnelStages(totalEggs, t)
        : primaryUnit === "embryos"
          ? embryoFunnelStages(thisCycleEmbryos, t, sourceCycleId ?? undefined)
          : eggFunnelStages(thisCycleEggs, t, sourceCycleId ?? undefined);

  /*
   * The first stage is her own figure — what the vault holds. Everything after
   * it is the projection's work.
   */
  /*
   * The caveat follows the figures on screen. The "this cycle" tab can show a
   * clinic-confirmed cohort while the vault as a whole holds something she
   * recorded herself, and printing "these figures are the ones you recorded"
   * under confirmed numbers is the wrong sentence.
   */
  const shownSelfRecordedCount =
    safeActive === "this-cycle" && cycleInventory
      ? cycleInventory.selfRecordedCount
      : vaultInventory.selfRecordedCount;

  const heldStages = stages.slice(0, 1);
  const estimateStages = stages.slice(1);
  const totalDots = stages[0]?.value ?? 1;

  return (
    <View>
      {vaultStaleRow}
      <FunnelScopeTabs
        options={options}
        active={safeActive}
        onChange={setActive}
      />
      {/*
       * One surface, from the tabs down.
       *
       * `attachedTop` exists to weld this card to the tabs above it, and a
       * heading placed between the two broke the join — leaving a strip of the
       * page's own grey showing through, which read as a seam rather than a
       * section. The card is drawn here instead, so the heading, her figure
       * and the estimate all sit inside it.
       */}
      <View
        style={{
          borderRadius: 16,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderWidth: 1,
          borderTopWidth: 0,
          borderColor: semanticColors.border.glass,
          backgroundColor: semanticColors.surface.subtle,
          padding: 8,
          gap: 6,
        }}
      >
        <MoonText
          variant="label"
          color="textMid"
          style={{ paddingHorizontal: 8, paddingTop: 4 }}
        >
          {t("homepage.review_concept.future_use_what_you_have")}
        </MoonText>
        <OutcomeDotFunnelRows
          stages={heldStages}
          totalDots={totalDots}
          embedded
        />
        {estimateStages.length > 0 ? (
          /*
           * The estimate gets a surface of its own.
           *
           * It had none: five rows of pale text floating straight on the glass
           * card's gradient, which is a poor place to read a number. Subordinate
           * content still has to be legible — the hierarchy is carried by the
           * header, the tint and the spacing, not by making the figures hard to
           * see. The shared panel sets it apart from her own count above, so
           * it reads as a block within the card rather than more of the same
           * list.
           */
          <CardSurface
            variant="panel"
            style={{
              marginTop: 6,
              paddingHorizontal: 10,
              paddingTop: 10,
              paddingBottom: 8,
              gap: 8,
            }}
          >
            {/*
             * The line where her record stops and the model starts.
             *
             * Above it is one number she gave; below it, five the app inferred
             * from population averages. Rendered as one list they read alike, so
             * "8 survive thawing" looked like a fact about her eggs rather than
             * an average applied to them. Naming the block is the smallest thing
             * that stops it, and folding it away is what lets her put the model
             * aside without losing the count she came for.
             */}
            <Accordion
              type="multiple"
              value={estimateOpen ? ["estimate"] : []}
              onValueChange={(value: string[]) =>
                setEstimateOpen(value.includes("estimate"))
              }
            >
              <AccordionItem value="estimate">
                <AccordionTrigger
                  analyticsLabel={t(
                    estimateOpen
                      ? "homepage.review_concept.future_use_hide_the_estimate"
                      : "homepage.review_concept.future_use_show_the_estimate",
                  )}
                  action="homepage.review_concept.future_use_estimate_from_population_averages"
                  className="items-center gap-1 py-0"
                >
                  {/*
                   * `textMid`, not `textFaint`: this names the section, and a
                   * heading nobody can read is not a quieter heading, it is an
                   * absent one. The control beside it stays faint, and that
                   * is where the hierarchy belongs.
                   */}
                  <MoonText
                    variant="label"
                    color="textMid"
                    style={{ flex: 1, marginRight: 8 }}
                  >
                    {t(
                      "homepage.review_concept.future_use_estimate_from_population_averages",
                    )}
                  </MoonText>
                  {/*
                   * `textLight`, not `textFaint`: this is the only affordance
                   * revealing five hidden rows, and the faint grey on a
                   * near-white panel is around 2:1. A control nobody can
                   * find is not a discreet control. It still sits below the
                   * heading it accompanies.
                   */}
                  <MoonText variant="caption" color="textLight">
                    {t(
                      estimateOpen
                        ? "homepage.review_concept.future_use_hide_the_estimate"
                        : "homepage.review_concept.future_use_show_the_estimate",
                    )}
                  </MoonText>
                </AccordionTrigger>
                <AccordionContent className="pb-0 pt-2">
                  <OutcomeDotFunnelRows
                    stages={estimateStages}
                    totalDots={totalDots}
                    // These rows continue the funnel; without the offset the
                    // dots restart at its lightest band and every stage reads
                    // one step shallower than it is.
                    stageIndexOffset={heldStages.length}
                    embedded
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardSurface>
        ) : null}
        {shownSelfRecordedCount > 0 ? (
          /*
           * What the projection rests on.
           *
           * A patient's own record is `unverified` by convention, and requiring
           * verification here is what used to empty this card entirely — she was
           * told her vault held nothing while the CryoVault screen listed
           * fifty-four frozen eggs. Her figures count. Saying where they come
           * from is the part that keeps the projection honest, rather than
           * dropping them or passing them off as confirmed.
           */
          <MoonText
            variant="caption"
            color="textLight"
            style={{ paddingTop: 10 }}
          >
            {t("homepage.review_concept.figures_are_the_ones_you_recorded")}
          </MoonText>
        ) : null}
      </View>
    </View>
  );
}
